import { IconType } from '@meronex/icons';
import {
  Box,
  Button,
  ButtonProps,
  Spinner,
  ThemeUIStyleObject,
} from '@apia/theme';
import simpleButton from '../../../themes/burocrat/styles/common/components/simpleButton';
import {
  forwardRef,
  MouseEvent,
  MouseEventHandler,
  Ref,
  useCallback,
  useMemo,
} from 'react';

export interface ISimpleButton extends ButtonProps {
  /** Allows to use a button to navigate */
  href?: string;
  icon?: IconType;
  iconPosition?: 'left' | 'right';
  iconSize?: number;
  isLoading?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  size?: 'sm' | 'md' | 'lg';
  variant?: string;
  spinnerSize?: string;
  type?: 'button' | 'submit' | 'reset';
}

const SimpleButton = (
  {
    children = null,
    className,
    disabled = false,
    href,
    icon,
    iconPosition = 'left',
    iconSize,
    id,
    isLoading,
    name,
    onClick,
    size,
    spinnerSize = '16',
    title,
    type = 'button',
    variant = 'primary',
    ...buttonProps
  }: ISimpleButton,
  ref: React.Ref<unknown>,
) => {
  const loadingSx = useMemo<ThemeUIStyleObject>(
    () => ({
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }),
    [],
  );
  const spanSx = useMemo(
    () => ({
      color: isLoading ? 'transparent' : undefined,
    }),
    [isLoading],
  );
  const Icon = useMemo(() => icon, [icon]);

  const innerOnClick = useCallback(
    (ev: MouseEvent<HTMLButtonElement>) => {
      if (href) {
        window.location.assign(href);
      }
      onClick?.(ev);
    },
    [href, onClick],
  );

  return (
    <Button
      ref={ref as Ref<HTMLButtonElement>}
      id={id}
      name={name}
      title={title}
      aria-label={title}
      onClick={innerOnClick}
      type={type}
      disabled={isLoading || disabled}
      className={className}
      {...window.getVariant(
        `${variant}${size !== 'md' && size ? `-${size}` : ''}`,
      )}
      sx={simpleButton}
      {...buttonProps}
    >
      {Icon && iconPosition === 'left' && <Icon size={iconSize} />}
      <Box as="span" className="button__content" sx={spanSx}>
        {children || title}
      </Box>
      {Icon && iconPosition === 'right' && <Icon size={iconSize} />}
      {isLoading && (
        <Box className="spinner" sx={loadingSx}>
          <Spinner size={spinnerSize as unknown as number} color="inherit" />
        </Box>
      )}
    </Button>
  );
};

export default forwardRef(SimpleButton);
